import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivityService, AlertService } from '@app/core';
import { Resp } from '@app/core/services/api.service';
import { ModalDailyTab, SidenavDailyShow } from '@app/modules/eclass/components/modal-daily/base';
import { SidenavDailyComponent } from '@app/modules/eclass/components/modal-daily/sidenav-daily.component';
import { SHARED_MODULES } from '@app/shared';
import { notNull } from '@app/shared/utils';
import { format } from 'date-fns';
import { isEmpty, isObject } from 'lodash';
import { catchError, finalize, map, Observable, of, switchMap, take, tap } from 'rxjs';

@Component({
  selector: 'app-modal-duplicate-activity',
  imports: [CommonModule, SHARED_MODULES, SidenavDailyComponent],
  templateUrl: './modal-duplicate-activity.component.html',
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalDuplicateActivityComponent {
  private readonly _activity = inject(DIALOG_DATA);
  private readonly _dialogRef = inject(DialogRef);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _alertService = inject(AlertService);
  private readonly _activityService = inject(ActivityService);

  protected targets: SidenavDailyShow[] = [];
  protected loading = signal<boolean>(false);

  protected get data(): any {
    return {
      type: ModalDailyTab.Activity,
      title: 'eclass.tarefa.duplicar-atividade',
      titleElement: this._activity.title,
      targetsLabel: 'eclass.tarefa.duplicar-para',
      publishLabel: 'eclass.tarefa.publicar',
      resetForm: () => null,
      markAllAsTouched: () => null,
      patchValue: () => null,
      onSave: this.onDuplicate,
      form: this._formBuilder.group({}),
      showSidenavEnd: false,
      sidenavEndData: null,
      targets: [],
      submitted$: of(),
      value: this._activity,
    };
  }

  protected onTargets(targets: SidenavDailyShow[]) {
    this.targets = targets;
  }

  public onDuplicate = (params: any): Observable<boolean> =>
    this._alertService
      .confirm({ message: 'eclass.tarefa.salvar' })
      .pipe(switchMap((confirm) => (confirm ? this.duplicate(params) : of(false))));

  protected onClose(): void {
    this._dialogRef.close();
  }

  protected onSave(): void {
    this.data
      .onSave({ ...this.data.value, targets: this.targets })
      .pipe(take(1), notNull())
      .subscribe(() => {
        this._dialogRef.close(true);
      });
  }

  private duplicate(params: any): Observable<boolean> {
    this.loading.set(true);
    const targets = params.targets.map((target: SidenavDailyShow) => ({
      id: target.id,
      entity_id: target.entity.id,
      group_id: target.group.id,
      subject_id: target.subject.id,
      date: format(target.date, 'yyyy-MM-dd'),
      students: this.resolveStudents(params, target),
    }));
    const data = {
      type: params.type,
      limit: params.limit,
      evaluative: params.evaluative,
      title: params.title,
      date_end: params.date_end,
      description: params.description,
      attachments: params.attachments,
      scale_id: params.evaluative ? params.scale_id : undefined,
      score: params.evaluative ? params.score : undefined,
      published: params.publish,
    };
    const store$ = this.store(targets, data);
    return store$.pipe(
      take(1),
      tap((result) => !!result && this._alertService.success({ message: 'geral.salvo-com-sucesso' })),
      finalize(() => this.loading.set(false)),
    );
  }

  private resolveStudents(params: any, target: SidenavDailyShow) {
    if (params.daily.group_id === target.group.id) {
      return (params.students || []).map(({ id }) => id);
    }
    return [];
  }

  private store(target: SidenavDailyShow[], data: any): Observable<boolean> {
    if (!isEmpty(target)) {
      return this.handleResponse(
        this._activityService.store({ ...data, attachments: data?.attachments.map(({ id, ...a }) => a), target }),
      );
    }
    return of(true);
  }

  private handleResponse(observable$: Observable<Resp<any>>): Observable<boolean> {
    return observable$.pipe(
      catchError((data) => {
        const message = data.msg || data.error?.msg;
        return of({ ret: 0, data: isObject(message) ? JSON.stringify(message) : message });
      }),
      switchMap((result) => {
        if (!result.ret) {
          const message = !!result.data ? { innerHtml: result.data } : { message: 'geral.erro-ao-salvar' };
          return this._alertService.alert(message).pipe(map(() => false));
        }
        return of(true);
      }),
    );
  }
}
